import { getAuth } from 'firebase/auth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { post } from 'src/api/requests';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { useRightHandSidebar } from 'src/contexts/RightHandSidebarContext';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useChatSync } from 'src/hooks/useChatSync';
import { Invitation } from 'src/interfaces/invitation.interface';
import { cn } from 'src/utils/className';
import { getPayloadJWT, isJWT } from 'src/utils/jwt';
import Cookies from 'universal-cookie';
import { Button } from './Button';
import { MintlifyAssistant } from './MintlifyAssistant';
import { PageTitle } from './PageTitle';
import RightHandSidebar from './RightHandSidebar';
import { Sidebar } from './Sidebar';

type LayoutProps = {
  children?: React.ReactNode;
};

const cookies = new Cookies(null, { path: '/' });
const DAYS_WARNING_THRESHOLD = 4;
const BANNER_HEIGHT = 40;
const BANNER_MARGIN = 48;

export const LoggedLayout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const { isOpen, sidebarWidth } = useRightHandSidebar();
  const defaultAuth = getAuth();
  const auth = useFirebaseAuth();
  const notification = useNotification();
  const [lockAt, setLockAt] = useState<Date>();
  const [mintlifyIsOpen, setMintlifyIsOpen] = useState(false);
  const location = useLocation();
  const { get } = useAuthedRequest();
  const { serviceKey } = useServiceMode();
  cookies.set('userId', auth?.user?.uid);
  const userEmail = auth?.user?.email;
  const [emailHash, setEmailHash] = useState<string | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>(
    [],
  );
  const [loadingHandleInvitation, setLoadingHandleInvitation] = useState(false);

  useChatSync({
    mintlifyIsOpen,
    setMintlifyIsOpen,
  });

  const daysLeft = lockAt
    ? Math.max(
        0,
        Math.floor(
          (lockAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [location.pathname]);

  useEffect(() => {
    if (process.env.REACT_APP_PYLON_APP_ID && userEmail) {
      const fetchEmailHash = async () => {
        try {
          const response = await get('/users/me/pylon-email-hash');
          setEmailHash(response.emailHash);
        } catch (error) {
          console.error('Error fetching email hash:', error);
          setEmailHash(null);
        }
      };

      fetchEmailHash();
    } else {
      setEmailHash(null);
    }
  }, [userEmail, get]);

  useEffect(() => {
    const user = auth?.user;
    if (user?.uid && !location.pathname.includes('/onboarding')) {
      window.pylon = {
        chat_settings: {
          app_id: process.env.REACT_APP_PYLON_APP_ID,
          email: user.email,
          name: user.displayName,
          avatar_url: user.photoURL,
          ...(emailHash && { email_hash: emailHash }),
        },
      };
    }
  }, [auth?.user, emailHash, location.pathname]);

  const fetchCurrentUserAndOrganization = useCallback(async () => {
    const token = await defaultAuth.currentUser?.getIdToken();

    if (token && isJWT(token) && !serviceKey) {
      const decoded = getPayloadJWT(token);

      if (decoded.organization) {
        try {
          const organization = await get(
            `/organizations/${decoded.organization.id}`,
          );
          if (organization?.lockAt) {
            setLockAt(new Date(organization.lockAt));
          }
        } catch (error) {
          console.error('Error fetching organization:', error);
        }
      }
    }
  }, [defaultAuth, get, serviceKey]);

  const fetchPendingInvitations = useCallback(async () => {
    const response = await get('/invitations');
    setPendingInvitations(response);
  }, [get]);

  useEffect(() => {
    if (auth?.user?.email) {
      fetchPendingInvitations();
    }
  }, [auth?.user?.email, fetchPendingInvitations]);

  useEffect(() => {
    if (!defaultAuth) {
      return;
    }

    fetchCurrentUserAndOrganization();
  }, [defaultAuth, fetchCurrentUserAndOrganization]);

  const resendVerificationEmail = useCallback(async () => {
    if (auth?.user) {
      try {
        await post('/users/me/verification-email', {});
        notification.success('Verification email sent');
      } catch {
        notification.error('Failed to send verification email');
      }
    }
  }, [auth, notification]);

  const handleAcceptInvitation = useCallback(
    async (organizationId: string) => {
      try {
        setLoadingHandleInvitation(true);
        await post('/invitations/accept', { organizationId });
        notification.success('Invitation accepted successfully');
        fetchPendingInvitations();
        navigate('/settings/organizations');
      } catch {
        notification.error('Failed to accept invitation');
      } finally {
        setLoadingHandleInvitation(false);
      }
    },
    [notification, fetchPendingInvitations],
  );

  const handleRejectInvitation = useCallback(
    async (organizationId: string) => {
      try {
        setLoadingHandleInvitation(true);
        await post('/invitations/reject', { organizationId });
        notification.success('Invitation rejected');
        fetchPendingInvitations();
      } catch {
        notification.error('Failed to reject invitation');
      } finally {
        setLoadingHandleInvitation(false);
      }
    },
    [notification, fetchPendingInvitations],
  );

  // Backend returns invitations ordered by createdAt DESC, so the first one is most recent
  const mostRecentInvitation =
    pendingInvitations.length > 0 ? pendingInvitations[0] : null;

  const banners = useMemo(() => {
    return [
      {
        id: 'plan-lock',
        isActive: !!lockAt,
        render: (topPosition: number) => (
          <div
            className={cn(
              'text-neutral-0 text-center p-2 text-sm lg:text-base fixed w-full z-10',
              daysLeft <= DAYS_WARNING_THRESHOLD ? 'bg-warning' : 'bg-primary',
            )}
            style={{ top: `${topPosition}px` }}
          >
            Welcome to Fluents.ai! You have <b>{daysLeft}</b> days left on your
            current plan
          </div>
        ),
      },
      {
        id: 'email-verification',
        isActive: !auth?.user?.emailVerified,
        render: (topPosition: number) => (
          <div
            className="text-neutral-0 text-center p-2 text-sm lg:text-base fixed w-full z-10 bg-primary-700"
            style={{ top: `${topPosition}px` }}
          >
            Please check your email to verify your account or click
            <a
              href="#"
              onClick={resendVerificationEmail}
              className="underline mx-1"
            >
              here
            </a>
            to resend the verification
          </div>
        ),
      },
      {
        id: 'invitation',
        isActive: !!mostRecentInvitation,
        render: (topPosition: number) => (
          <div
            className={cn(
              'text-neutral-0 text-center p-2 text-sm lg:text-base fixed w-full z-10 bg-info-500 flex items-center justify-center gap-2',
              loadingHandleInvitation ? 'opacity-50' : 'opacity-100',
            )}
            style={{ top: `${topPosition}px` }}
          >
            <span>
              You have been invited to join{' '}
              <b>
                {mostRecentInvitation!.organization?.name || 'an organization'}:
              </b>
            </span>
            <div className="flex gap-0.5">
              <Button
                variant="text"
                color="secondary"
                onClick={() =>
                  handleAcceptInvitation(mostRecentInvitation!.organizationId)
                }
                className="underline cursor-pointer m-0 p-0 px-1 text-neutral-0 hover:text-primary-400"
              >
                Accept
              </Button>
              <span>or</span>
              <Button
                variant="text"
                color="secondary"
                onClick={() =>
                  handleRejectInvitation(mostRecentInvitation!.organizationId)
                }
                className="underline cursor-pointer m-0 p-0 px-1 text-neutral-0 hover:text-primary-400"
              >
                Reject
              </Button>
            </div>
          </div>
        ),
      },
    ];
  }, [
    lockAt,
    daysLeft,
    auth?.user?.emailVerified,
    mostRecentInvitation,
    loadingHandleInvitation,
    resendVerificationEmail,
    handleAcceptInvitation,
    handleRejectInvitation,
  ]);

  const activeBanners = useMemo(
    () => banners.filter((banner) => banner.isActive),
    [banners],
  );

  return (
    <div className="flex flex-col bg-neutral-50">
      <PageTitle />
      {activeBanners.map((banner, index) => (
        <React.Fragment key={banner.id}>
          {banner.render(index * BANNER_HEIGHT)}
        </React.Fragment>
      ))}

      <div
        className={cn('flex m-2 bg-neutral-0 rounded-xl', 'min-h-[98vh]')}
        style={
          activeBanners.length === 0
            ? {}
            : {
                minHeight: `calc(97vh - ${activeBanners.length * BANNER_HEIGHT}px)`,
                marginTop: `${activeBanners.length * BANNER_MARGIN}px`,
              }
        }
      >
        <Sidebar />

        <div className={cn('w-full block border-l border-neutral-100')}>
          <div
            className={cn(
              'grid w-full transition-all duration-300',
              isOpen
                ? 'grid-cols-[1fr_var(--sidebar-width)]'
                : 'grid-cols-[1fr_0]',
            )}
            style={
              {
                '--sidebar-width': sidebarWidth,
              } as React.CSSProperties
            }
          >
            <div
              className={cn(
                'transition-all duration-300 flex flex-1 overflow-y-auto',
              )}
            >
              <div
                className={cn(
                  'bg-neutral-0 w-full',
                  !isOpen ? 'rounded-r-xl' : 'rounded-r-none',
                  'overflow-auto',
                )}
              >
                {children || <Outlet />}
              </div>
            </div>

            <RightHandSidebar />
          </div>
        </div>
      </div>
      {process.env.REACT_APP_MINTLIFY_DOMAIN &&
        process.env.REACT_APP_MINTLIFY_TOKEN && (
          <MintlifyAssistant
            domain={process.env.REACT_APP_MINTLIFY_DOMAIN}
            docsURL={process.env.REACT_APP_MINTLIFY_DOCS_URL}
            token={process.env.REACT_APP_MINTLIFY_TOKEN}
            userId={auth?.user?.uid}
            isOpen={mintlifyIsOpen}
            setIsOpen={setMintlifyIsOpen}
          />
        )}
    </div>
  );
};
